import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
var _excluded = ["as", "children"],
    _excluded2 = ["imageSrc", "alt", "providerNumber", "clickable", "variant"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useMemo } from 'react';
import ConditionalHydrate from '@uswitch/ev-utils/conditional-hydrate';
import { useEntity, useBrand, useMeta, useDataSelector } from '@uswitch/ev-utils/context/selectors';
import { Type } from '@uswitch/spark.type';
import ImgixImg from '../../elements/imgix-img';
import withComponentBase from '../../helpers/with-component-base';
import { UswArrowLargeRight } from '@uswitch/spark-icons/usw';
import { Button } from '@uswitch/spark';
import { jsx as _jsx } from "theme-ui/jsx-runtime";
import { jsxs as _jsxs } from "theme-ui/jsx-runtime";
var maxMobileProviderNumbers = 3;
var providersUrl = 'https://www.uswitch.com/broadband/providers/';

var shuffle = function shuffle() {
  var array = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  array.forEach(function (_, index) {
    var randomIndex = Math.floor(Math.random() * index);
    var _ref = [array[randomIndex], array[index]];
    array[index] = _ref[0];
    array[randomIndex] = _ref[1];
  });
  return array;
};

var sortProviders = function sortProviders(a, b) {
  return a.name < b.name ? -1 : 1;
};

var createGridConfiguration = function createGridConfiguration(numberOfItemsToDisplay, variant) {
  var maxItemsPerRow = 6;
  var maxNumberOfRows = 3;

  var _distributeColumnsAcr = distributeColumnsAcrossRows(numberOfItemsToDisplay, maxItemsPerRow, maxNumberOfRows),
      rows = _distributeColumnsAcr.rows,
      columnsPerRow = _distributeColumnsAcr.columnsPerRow;

  return {
    rows: rows,
    columnsPerRow: columnsPerRow,
    columnStyle: {
      gridTemplateColumns: ["repeat(".concat(maxMobileProviderNumbers * 2, ", minmax(0, 1fr))"), "repeat(".concat(maxItemsPerRow * 2, ", minmax(0, 1fr))")]
    }
  };
};

var distributeColumnsAcrossRows = function distributeColumnsAcrossRows(numberOfItemsToDisplay, maxNumberOfItemsPerRow, maxNumberOfRows) {
  var multipleRowsRequired = numberOfItemsToDisplay > maxNumberOfItemsPerRow;
  var isSingleColumn = maxNumberOfItemsPerRow === 1;

  if (isSingleColumn) {
    return {
      columnsPerRow: 2,
      rows: numberOfItemsToDisplay,
      finalRowColumns: 0
    };
  }

  if (!multipleRowsRequired) {
    return {
      columnsPerRow: maxNumberOfItemsPerRow,
      rows: 1,
      finalRowColumns: 0
    };
  }

  var currentSmallestDifferenceByItemsPerRow;

  for (var itemsPerRow = maxNumberOfItemsPerRow; itemsPerRow > 1; itemsPerRow--) {
    var itemsInFinalRow = numberOfItemsToDisplay % itemsPerRow;
    var differenceBetweenLastTwoRows = itemsPerRow - itemsInFinalRow;
    var numberOfRowsRequired = Math.ceil(numberOfItemsToDisplay / maxNumberOfItemsPerRow);
    var itemsPerRowDoesNotExceedMaxPerRow = itemsPerRow <= maxNumberOfItemsPerRow; // No better possibilites available within row restraints

    if (numberOfRowsRequired > maxNumberOfRows) {
      if (currentSmallestDifferenceByItemsPerRow) {
        return currentSmallestDifferenceByItemsPerRow;
      } else {
        return {
          columnsPerRow: maxNumberOfItemsPerRow,
          rows: maxNumberOfRows,
          finalRowColumns: maxNumberOfItemsPerRow
        };
      }
    }

    if (itemsInFinalRow === 0 && itemsPerRowDoesNotExceedMaxPerRow) {
      return {
        columnsPerRow: maxNumberOfItemsPerRow,
        rows: numberOfRowsRequired,
        finalRowColumns: itemsInFinalRow
      };
    }

    if (!currentSmallestDifferenceByItemsPerRow || currentSmallestDifferenceByItemsPerRow.columnsPerRow > itemsInFinalRow && currentSmallestDifferenceByItemsPerRow.differenceBetweenLastTwoRows > differenceBetweenLastTwoRows) {
      currentSmallestDifferenceByItemsPerRow = {
        columnsPerRow: maxNumberOfItemsPerRow,
        rows: numberOfRowsRequired,
        finalRowColumns: itemsInFinalRow,
        differenceBetweenLastTwoRows: differenceBetweenLastTwoRows
      };
    } // Final iteration


    if (itemsPerRow === 2) {
      return currentSmallestDifferenceByItemsPerRow;
    }
  }
};

var overrideProviderOrder = function overrideProviderOrder(providers, overrideOrder) {
  var overridePriority = _toConsumableArray(overrideOrder).reverse();

  var reOrderedProviders = _toConsumableArray(providers);

  overridePriority.forEach(function (override) {
    var indexOfOverride = reOrderedProviders.findIndex(function (provider) {
      return provider.name.toLowerCase() === override.toLowerCase();
    });

    if (indexOfOverride !== -1) {
      reOrderedProviders.unshift(reOrderedProviders.splice(indexOfOverride, 1)[0]);
    }
  });
  return reOrderedProviders;
};

export var ProvidersLogoModule = function ProvidersLogoModule(_ref2) {
  var providers = _ref2.providers,
      orderOverride = _ref2.orderOverride,
      _ref2$clickable = _ref2.clickable,
      clickable = _ref2$clickable === void 0 ? false : _ref2$clickable,
      variant = _ref2.variant,
      maxProviders = _ref2.maxProvidersToDisplay;
  var brand = useBrand();
  var defaultMaxProviders = 10;
  var metaMaxProviders = useMeta(['providers', 'maxProvidersToDisplay'], defaultMaxProviders);
  var maxProvidersToDisplay = maxProviders || metaMaxProviders;
  var isCi = useDataSelector(['isCi']);
  var shuffledProviders = useMemo(function () {
    return shuffle(providers);
  }, [providers]);

  if (!providers) {
    return null;
  }

  if (isCi) shuffledProviders = providers === null || providers === void 0 ? void 0 : providers.sort(sortProviders);
  var orderedProviders = (orderOverride ? overrideProviderOrder(shuffledProviders, orderOverride) : shuffledProviders).slice(0, maxProvidersToDisplay);
  var sufficientProvidersToDisplay = orderedProviders.length >= 3;

  var _createGridConfigurat = createGridConfiguration(orderedProviders.length, variant),
      rows = _createGridConfigurat.rows,
      columnsPerRow = _createGridConfigurat.columnsPerRow,
      columnStyle = _createGridConfigurat.columnStyle;

  return _jsx(ConditionalHydrate, {
    children: sufficientProvidersToDisplay ? [_jsx("div", {
      sx: {
        display: ['flex', 'grid'],
        overflowX: ['auto', 'hidden'],
        scrollSnapType: 'x mandatory',
        scrollBehavior: 'smooth',
        wordWrap: 'nowrap'
      },
      children: Array(rows).fill().map(function (row, i) {
        return _jsx("div", {
          sx: _objectSpread(_objectSpread({
            display: ['inline-flex', 'grid'],
            gridColumnGap: '20px'
          }, columnStyle), {}, {
            mb: 20,
            ml: i !== 0 ? [20, 0] : 0,
            whiteSpace: ['pre', 'initial']
          }),
          children: orderedProviders.slice(i * columnsPerRow, (i + 1) * columnsPerRow).map(function (provider, j) {
            return _jsx(Provider, {
              imageSrc: provider.images[brand] || provider.images.logo,
              alt: provider.name,
              providerNumber: i * columnsPerRow + j + 1,
              clickable: clickable,
              href: clickable ? provider.link || provider.slug : undefined,
              variant: variant
            }, provider.id);
          }, this)
        }, i);
      }, this)
    }, "0"), variant === 'broadband' ? [_jsx(Button, {
      as: "a",
      variant: "secondary",
      size: "medium",
      rightIcon: _jsx(UswArrowLargeRight, {
        strokeWidth: "2px"
      }),
      href: providersUrl,
      sx: {
        display: 'inline-flex',
        float: ['left', 'right'],
        fontWeight: '700'
      },
      children: "See all providers"
    }, "1-0"), _jsx("div", {
      style: {
        clear: 'both',
        marginBottom: '20px'
      }
    }, "1-1")] : null] : null
  });
};

var ProviderWrapper = function ProviderWrapper(_ref3) {
  var Component = _ref3.as,
      children = _ref3.children,
      props = _objectWithoutProperties(_ref3, _excluded);

  return _jsx(Component, _objectSpread(_objectSpread({}, props), {}, {
    children: children
  }));
};

var Provider = function Provider(_ref4) {
  var imageSrc = _ref4.imageSrc,
      alt = _ref4.alt,
      providerNumber = _ref4.providerNumber,
      clickable = _ref4.clickable,
      variant = _ref4.variant,
      props = _objectWithoutProperties(_ref4, _excluded2);

  return _jsxs(ProviderWrapper, _objectSpread(_objectSpread({
    as: clickable ? 'a' : 'div',
    sx: _objectSpread(_objectSpread({
      flex: 1,
      display: 'flex',
      justifyContent: 'center',
      gridColumn: 'span 2',
      padding: ['sm', 'md'],
      border: '1px solid',
      borderColor: 'grey-20',
      flexDirection: 'column',
      alignItems: 'center',
      color: 'primary'
    }, clickable && {
      '&:active': {
        borderColor: 'primary'
      },
      '&:hover': {
        borderColor: 'primary'
      },
      '&:focus': {
        borderColor: 'primary'
      }
    }), {}, {
      width: ['120px', 'auto'],
      scrollSnapAlign: 'center'
    })
  }, props), {}, {
    children: [_jsx(ImgixImg, {
      src: imageSrc,
      alt: alt,
      sx: _objectSpread(_objectSpread({
        maxWidth: ['70px', '102px'],
        maxHeight: ['42px', '48px'],
        width: 'auto',
        height: 'auto',
        margin: '10px',
        alignSelf: 'center'
      }, clickable && {
        margin: 0,
        mb: 'sm'
      }), 1 && {
        margin: ['20px 0', 0],
        mb: ['20px', 'sm']
      }),
      height: 60,
      width: 120,
      imgixParams: {
        fit: 'fill',
        fill: 'solid'
      }
    }), clickable && variant !== 'no-link-text' ? _jsx(Type.Body1, {
      sx: {
        textAlign: 'center',
        whiteSpace: 'break-spaces',
        wordWrap: 'break-spaces',
        fontSize: '16px'
      },
      children: alt
    }) : null]
  }));
};

var ProvidersLogoModuleConnected = function ProvidersLogoModuleConnected(_ref5) {
  var dataset = _ref5.dataset,
      orderOverride = _ref5.orderOverride,
      _ref5$clickable = _ref5.clickable,
      clickable = _ref5$clickable === void 0 ? false : _ref5$clickable,
      variant = _ref5.variant,
      maxProvidersToDisplay = _ref5.maxProvidersToDisplay;

  var _useEntity = useEntity(dataset),
      providers = _useEntity.data;

  return _jsx(ProvidersLogoModule, {
    providers: providers,
    orderOverride: orderOverride,
    clickable: clickable,
    variant: variant,
    maxProvidersToDisplay: maxProvidersToDisplay
  });
};

export default withComponentBase(ProvidersLogoModuleConnected, {
  withHeaderFooter: true,
  headerStyles: _objectSpread(_objectSpread({}, {
    textAlign: 'left',
    paddingBottom: 'sm',
    fontWeight: 'bold'
  }), {}, {
    '*:last-child': {
      paddingBottom: 0
    },
    my: 16
  })
});
//# sourceMappingURL=index.js.map